import { cn } from '@/lib/cn';
import { useT } from '@/lib/i18n/useT';
import { resolveImageUrl } from '@/lib/images/resolveImageUrl';
import type { EventFactsLedgerRow } from '../factsApi';
import { fmtInt, initials } from '../engine';
import { useScrollRamp } from '../motion/useScrollRamp';

/**
 * El muro de premios de Track: por artículo, cuántos quedan en juego, la
 * barra de lo que queda sobre lo asignado y el aviso de reposición. Oscuro
 * porque se mira en una pantalla del recinto, de lejos.
 */
export function PrizeWall({ rows: allRows, header, className }: { rows: EventFactsLedgerRow[]; header?: React.ReactNode; className?: string }) {
  const t = useT();
  // Un artículo sin asignación no está en el muro: no hay nada que reponer.
  const rows = allRows.filter((r) => r.allocated > 0 || r.inPlay > 0);
  const { ref, p } = useScrollRamp();
  return (
    <section ref={ref} className={cn('rounded-2xl bg-[var(--color-chrome-bg)] p-5 text-[var(--color-chrome-fg)]', className)}>
      <div className="mb-4 flex flex-wrap items-center justify-between gap-3">
        <h3 className="text-base font-medium">{t('dash.wall.title')}</h3>
        {header}
      </div>
      {rows.length === 0 ? (
        <p className="text-sm text-[var(--color-chrome-fg-muted)]">{t('dash.wall.empty')}</p>
      ) : (
        <div className="grid gap-3 [grid-template-columns:repeat(auto-fit,minmax(200px,1fr))]">
          {rows.map((row) => {
            const pct = row.allocated ? (row.inPlay / row.allocated) * 100 : 0;
            const threshold = row.restockThreshold ?? 0;
            const zero = row.inPlay <= 0;
            const low = !zero && threshold > 0 && row.inPlay <= threshold;
            const tone = zero ? 'var(--color-error)' : low ? 'var(--color-warn)' : 'var(--color-chrome-accent)';
            return (
              <div
                key={row.stockId}
                className={cn(
                  'flex flex-col gap-2.5 rounded-xl border p-4',
                  low || zero ? 'border-[color-mix(in_srgb,var(--color-warn)_40%,transparent)]' : 'border-[var(--color-chrome-border)]',
                  'bg-[var(--color-chrome-bg-elevated)]',
                )}
              >
                <div className="flex items-center gap-2.5">
                  {row.image ? (
                    <img src={resolveImageUrl(row.image ?? undefined)} alt="" className="h-9 w-9 rounded-lg object-cover" />
                  ) : (
                    <span className="flex h-9 w-9 items-center justify-center rounded-lg font-mono text-xs font-bold" style={{ background: 'var(--color-chrome-accent-soft)', color: tone }}>
                      {initials(row.name)}
                    </span>
                  )}
                  <span className="min-w-0 truncate text-sm font-semibold">{row.name}</span>
                </div>
                <div className="flex items-end gap-2">
                  <span className="text-[2.5rem] font-bold leading-none tabular-nums" style={{ color: zero || low ? tone : 'var(--color-chrome-fg)' }}>
                    {fmtInt(Math.round(row.inPlay * p))}
                  </span>
                  <span className="pb-1 text-xs text-[var(--color-chrome-fg-muted)]">{t('dash.wall.inPlay')}</span>
                </div>
                <div className="h-1.5 overflow-hidden rounded-full bg-[var(--color-chrome-border)]">
                  <div className="h-full rounded-full transition-[width] duration-500" style={{ width: `${Math.max(2, pct * p)}%`, background: tone }} />
                </div>
                <span
                  className="self-start rounded-full px-2.5 py-0.5 font-mono text-[9px] font-bold uppercase tracking-wider"
                  style={{ background: `color-mix(in srgb, ${tone} 18%, transparent)`, color: tone }}
                >
                  {zero ? t('dash.wall.out') : low ? t('dash.wall.restock') : t('dash.wall.healthy')}
                </span>
                <span className="text-[11px] text-[var(--color-chrome-fg-subtle)]">
                  {t('dash.wall.delivered', { d: fmtInt(row.delivered), a: fmtInt(row.allocated) })}
                </span>
              </div>
            );
          })}
        </div>
      )}
    </section>
  );
}
